import { Fragment } from "react";
import { alpha, Box, ButtonBase, Divider, Menu, MenuItem, Theme, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DotLegalButton, DotLegalChip, DotLegalHeader, DotLegalInformationTooltip, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { FormulaEditorProps, useFormulaEditor } from "./FormulaEditor.hooks.ts";
import { formulaOperators, operatorSymbols } from "./calculatedField.utils.ts";
import { fieldMenuItemSx, hintSx } from "./calculatedField.styles.ts";
import FormulaFieldPopover from "./FormulaFieldPopover.tsx";
import ClearFormulaIcon from "./clear.svg?react";
import RemoveLastIcon from "./removeLast.svg?react";

const operatorButtons = formulaOperators.map((operator) => ({
    operator,
    symbol: operatorSymbols[operator],
}));

// The space between tokens in the box; the caret's margins cancel one of them so it takes no room of its own
const tokenGap = 0.75;

const helperLineSx = { ...hintSx, display: "flex", alignItems: "center", gap: 0.5, marginTop: 1 } as const;
const helperIconSx = { width: 14, height: 14 } as const;
const symbolTextSx = { fontSize: 18, color: "primary.dark" } as const;
const symbolSx = { ...symbolTextSx, fontWeight: 600 } as const;
const symbolButtonSx = {
    ...symbolTextSx,
    minWidth: 28,
    height: 28,
    borderRadius: 1,
    transition: "background-color 150ms",
    "&:hover": { backgroundColor: (theme: Theme) => alpha(theme.palette.primary.dark, 0.08) },
    "&.Mui-disabled": { opacity: 0.5 },
} as const;
const toolbarDividerSx = { marginX: 0.5, height: 20, alignSelf: "center" } as const;
// Blinks like the gap cursor in MentionInput, so the formula box reads as something you type in. It takes no width, so the
// tokens stay put when it moves; the line itself is drawn outside the flow
const caretSx = {
    display: "none",
    position: "relative",
    width: 0,
    height: 18,
    marginX: -tokenGap / 2,
    animation: "formulaCaretBlink 1.1s steps(2, start) infinite",
    "@keyframes formulaCaretBlink": { to: { visibility: "hidden" } },
    "&::after": { content: '""', position: "absolute", top: 0, bottom: 0, left: "-0.75px", width: "1.5px", backgroundColor: "primary.dark" },
} as const;
// DotLegalChip forces cursor: default on hover unless it gets its own onClick, so the hover rule is overridden here
const fieldTokenSx = { "& .MuiChip-root:hover": { cursor: "pointer" } } as const;
const replaceableSx = { cursor: "pointer" } as const;
const misplacedSymbolSx = { color: "error.main" } as const;
const misplacedChipSx = { "& .MuiChip-root": { outlineColor: "error.main", borderColor: "error.main" } } as const;

// Same chip look as the multi-selects, so a field in a formula reads like a chosen value anywhere else
const chipDeletionIcon = <ClearIcon sx={{ width: 16, height: 16 }} />;

function FormulaEditor(props: FormulaEditorProps) {
    const { translateString } = useTranslation();
    const {
        chips,
        caret,
        tokenBoxRef,
        addFieldAnchorRef,
        fieldOptions,
        replaceMenu,
        closeReplaceMenu,
        replaceWith,
        isFieldPickerOpen,
        openFieldPicker,
        closeFieldPicker,
        allowedNextTokens,
        operatorsEnabled,
        incompleteHint,
        showIncompleteAsError,
        example,
        addField,
        addOperator,
        addBracket,
        isEmpty,
        clearFormula,
        removeLastToken,
        placeCaret,
        keepFocusInTokenBox,
        onKeyDown,
        onBlur,
    } = useFormulaEditor(props);

    const caretElement = <Box component="span" className="formulaCaret" sx={caretSx} />;

    return (
        <Box onBlur={onBlur}>
            <DotLegalHeader headerStyle="extraSmall" marginBottom={1}>
                {props.fieldName}
            </DotLegalHeader>

            <Box sx={{ position: "relative" }}>
                <Box
                    sx={(theme) => ({
                        border: `1px solid ${showIncompleteAsError ? theme.palette.error.main : theme.palette.grey[300]}`,
                        borderRadius: 2,
                        overflow: "hidden",
                        "&:focus-within": showIncompleteAsError ? undefined : { borderColor: theme.palette.primary.main },
                    })}
                >
                    <Box
                        ref={tokenBoxRef}
                        tabIndex={0}
                        onKeyDown={onKeyDown}
                        onClick={placeCaret}
                        sx={(theme) => ({
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: tokenGap,
                            minHeight: 56,
                            padding: 1.5,
                            outline: "none",
                            cursor: "text",
                            // The caret and the matching brackets belong to the keyboard, so they only show while the box has focus.
                            // The highlight's padding is cancelled by its margin so the tokens stay put when it appears
                            "&:focus .formulaCaret": { display: "block" },
                            "&:focus .formulaBracketMatch": {
                                backgroundColor: alpha(theme.palette.primary.main, 0.15),
                                borderRadius: 1,
                                paddingX: 0.25,
                                marginX: -0.25,
                            },
                        })}
                    >
                        {chips.map((chip) => (
                            <Fragment key={chip.index}>
                                {chip.index === caret && caretElement}
                                <Box
                                    component="span"
                                    data-token
                                    className={chip.isHighlightedBracket ? "formulaBracketMatch" : undefined}
                                    sx={[
                                        chip.isField ? fieldTokenSx : symbolSx,
                                        !!chip.onClick && replaceableSx,
                                        chip.isMisplaced && (chip.isField ? misplacedChipSx : misplacedSymbolSx),
                                    ]}
                                    onClick={chip.onClick}
                                >
                                    {chip.isField ? (
                                        <DotLegalChip value={chip.text} size="small" isAutocomplete deletionIcon={chipDeletionIcon} onDelete={chip.onDelete} />
                                    ) : (
                                        chip.text
                                    )}
                                </Box>
                            </Fragment>
                        ))}
                        {caret === chips.length && caretElement}
                        {isEmpty && <Typography sx={{ fontSize: 14, color: "text.secondary" }}>{translateString("formulaEmptyState")}</Typography>}
                        <Menu anchorEl={replaceMenu?.anchor} open={!!replaceMenu} onClose={closeReplaceMenu}>
                            {replaceMenu?.options.map((option) => (
                                <MenuItem key={option.id} selected={option.isSelected} onClick={() => replaceWith(option.id)} sx={fieldMenuItemSx}>
                                    <span>{option.name}</span>
                                    {option.hint && (
                                        <Box component="span" sx={hintSx}>
                                            {option.hint}
                                        </Box>
                                    )}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>

                    <Box
                        onMouseDown={keepFocusInTokenBox}
                        sx={(theme) => ({
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: 0.5,
                            padding: 1,
                            backgroundColor: theme.palette.grey[50],
                            borderTop: `1px solid ${theme.palette.grey[200]}`,
                            // DotLegalButton has no colour prop, so Add field and Clear are recoloured to match the symbol buttons
                            "& .MuiButton-root": { color: theme.palette.primary.dark },
                            // Doubled "&&" because DotLegalButton's own disabled selector ties with a single "&" and wins
                            "&& .MuiButton-root.Mui-disabled": { color: alpha(theme.palette.primary.dark, 0.5) },
                        })}
                    >
                        <Box ref={addFieldAnchorRef} sx={{ button: { height: "30px" } }}>
                            <DotLegalButton
                                buttonType="linkButton"
                                size="small"
                                // The theme fills icons blue like the info icon, but the + belongs to the navy Add field label
                                startIcon={<AddIcon sx={{ fill: "currentColor" }} />}
                                disabled={!allowedNextTokens.field}
                                onClick={openFieldPicker}
                            >
                                {translateString("addField")}
                            </DotLegalButton>
                        </Box>
                        <Divider orientation="vertical" flexItem sx={toolbarDividerSx} />
                        {operatorButtons.map((button) => (
                            <ButtonBase key={button.operator} sx={symbolButtonSx} disabled={!operatorsEnabled} onClick={() => addOperator(button.operator)}>
                                {button.symbol}
                            </ButtonBase>
                        ))}
                        <ButtonBase sx={symbolButtonSx} disabled={!allowedNextTokens.openBracket} onClick={() => addBracket(true)}>
                            (
                        </ButtonBase>
                        <ButtonBase sx={symbolButtonSx} disabled={!allowedNextTokens.closeBracket} onClick={() => addBracket(false)}>
                            )
                        </ButtonBase>
                        <Box sx={{ flex: 1 }} />
                        <Typography sx={hintSx}>{translateString("formulaPrecedenceShortHint")}</Typography>
                        <Divider orientation="vertical" flexItem sx={toolbarDividerSx} />
                        <DotLegalTooltip text={translateString("removeLast")}>
                            {/* A disabled button fires no mouse events, so the tooltip listens on a wrapper */}
                            <Box component="span">
                                <ButtonBase sx={symbolButtonSx} disabled={isEmpty} onClick={removeLastToken} aria-label={translateString("removeLast")}>
                                    <RemoveLastIcon width={20} height={11.67} />
                                </ButtonBase>
                            </Box>
                        </DotLegalTooltip>
                        <Divider orientation="vertical" flexItem sx={toolbarDividerSx} />
                        <DotLegalButton
                            buttonType="linkButton"
                            size="small"
                            // DotLegalButton only dims the text when disabled, and the icon's colours are baked into the SVG
                            startIcon={<ClearFormulaIcon width={17} height={15} style={{ opacity: isEmpty ? 0.5 : 1 }} />}
                            disabled={isEmpty}
                            onClick={clearFormula}
                        >
                            {translateString("clear")}
                        </DotLegalButton>
                    </Box>
                </Box>

                {props.fieldDescription && (
                    <Box sx={{ position: "absolute", right: -31, top: 10 }}>
                        <DotLegalInformationTooltip text={props.fieldDescription} />
                    </Box>
                )}
            </Box>

            <FormulaFieldPopover
                anchorEl={addFieldAnchorRef.current}
                open={isFieldPickerOpen}
                options={fieldOptions}
                onSelect={addField}
                onClose={closeFieldPicker}
            />

            {isEmpty && (
                <Typography sx={helperLineSx}>
                    <InfoOutlinedIcon sx={helperIconSx} />
                    {translateString("formulaHelperText")}
                </Typography>
            )}
            {incompleteHint && (
                <Typography sx={{ ...helperLineSx, color: showIncompleteAsError ? "error.main" : "text.secondary" }}>{incompleteHint}</Typography>
            )}
            {example && (
                <Typography sx={helperLineSx}>
                    <CheckIcon sx={helperIconSx} />
                    {translateString("formulaExample", { example })}
                </Typography>
            )}
        </Box>
    );
}

export default FormulaEditor;
